import liff from '@line/liff'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { getApiV1WeddingsByWeddingIdAuthMeOptions } from '@wedding-tech/api-client/tanstack'
import { Button } from '@wedding-tech/ui/button'
import { Card, CardContent } from '@wedding-tech/ui/card'
import {
  Calendar,
  ChevronRight,
  Gift,
  Heart,
  Map as MapIcon,
  MapPin,
  Navigation,
  QrCode,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { useSettings } from '@/hooks/useSettings'
import { formatThaiDate } from '@/lib/date'
import { cn } from '@/lib/utils'
import { useEventCopy } from '@/lib/utils/eventCopy'
import MaterialSymbolsNavigationRounded from '~icons/material-symbols/navigation-rounded'
import MdiDice5Outline from '~icons/mdi/dice-5-outline'

export const UserDashboardPage = () => {
  const { eventId } = useParams({ from: '/e/$eventId/_dashboard/' })
  const { data: authMe, isLoading: isAuthLoading } = useQuery(
    getApiV1WeddingsByWeddingIdAuthMeOptions({ path: { weddingId: eventId } }),
  )
  const {
    settings,
    isCheckInOpen,
    isLoading: isSettingsLoading,
  } = useSettings()
  const copy = useEventCopy()

  const profile = authMe?.profile
  const checkIn = authMe?.checkInDetail
  const status = authMe?.status

  const tableNumber = status?.hasCheckedIn
    ? checkIn?.tableAssignment?.tableNumber || (checkIn as any)?.table || null
    : null
  const seatDisplay = status?.hasCheckedIn
    ? checkIn?.tableAssignment?.seatDisplay || null
    : null

  const [currentAgendaId, setCurrentAgendaId] = useState<string | null>(null)

  const s = settings as any

  // Use dynamic agenda from settings if available
  const agendaList = useMemo(() => {
    return s?.agenda || []
  }, [s])

  // Real-time dynamic agenda active item logic
  useEffect(() => {
    const updateCurrent = () => {
      const now = new Date()
      // Use event date matching if configured
      if (s?.weddingDate) {
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const currentDateStr = `${year}-${month}-${day}`
        if (currentDateStr !== s.weddingDate) {
          setCurrentAgendaId(null)
          return
        }
      }

      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      let activeId: string | null = null

      for (let i = 0; i < agendaList.length; i++) {
        const item = agendaList[i]
        const nextItem = agendaList[i + 1]

        if (currentTimeStr >= item.time) {
          if (!nextItem || currentTimeStr < nextItem.time) {
            activeId = item.id
            break
          }
        }
      }

      setCurrentAgendaId(activeId)
    }

    updateCurrent()
    const interval = setInterval(updateCurrent, 60000)
    return () => clearInterval(interval)
  }, [agendaList, s?.weddingDate])

  if (isAuthLoading || isSettingsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-stone-500">
        กำลังโหลดข้อมูล...
      </div>
    )
  }

  const displayWeddingDate = s?.weddingDate ? formatThaiDate(s.weddingDate) : ''

  const displayLocation = s?.weddingLocation || ''
  const displayLocationDetail = ''

  // Modules visibility checks
  const showCheckIn = s?.modules?.checkIn !== false
  const showBlessing = s?.modules?.blessing !== false
  const showLuckyDraw = s?.modules?.luckyDraw !== false
  const showAngpao = s?.modules?.angpao !== false

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <main className="flex-1 space-y-6 overflow-auto px-6 py-8">
        {/* Welcome Section */}
        <section className="space-y-1">
          <h2 className="text-muted-foreground text-sm font-medium tracking-wider uppercase">
            ยินดีต้อนรับ
          </h2>
          <h1 className="text-3xl font-bold text-stone-900">
            สวัสดีคุณ {profile?.fullName || profile?.displayName}
          </h1>
        </section>

        {/* Status Card (Only shown if Check-in module is enabled) */}
        {showCheckIn && (
          <section>
            {status?.hasCheckedIn ? (
              <Card className="overflow-hidden border-none bg-emerald-50 text-emerald-950">
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <QrCode className="size-6" />
                  </div>
                  <div>
                    <p className="font-bold">เช็กอินเรียบร้อยแล้ว</p>
                    <p className="text-sm text-emerald-700">
                      ขอให้มีความสุขใน{copy.eventName}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-primary/5 overflow-hidden border-none">
                <CardContent className="flex flex-col gap-4 p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 text-primary flex size-12 items-center justify-center rounded-full">
                      <Calendar className="size-6" />
                    </div>
                    <div>
                      <p className="font-bold text-stone-900">
                        ยินดีต้อนรับสู่งาน{copy.eventName}
                      </p>
                      <p className="text-sm text-stone-500">
                        {isCheckInOpen
                          ? 'กรุณาเช็กอิน ณ สถานที่จัดงานเพื่อลงทะเบียนเข้าร่วมงาน'
                          : 'กรุณาติดต่อเจ้าหน้าที่ ณ สถานที่จัดงาน'}
                      </p>
                    </div>
                  </div>
                  {isCheckInOpen && (
                    <Button
                      render={
                        <Link
                          to="/e/$eventId/checkin"
                          params={{ eventId }}
                        />
                      }
                      nativeButton={false}
                      className="bg-primary shadow-primary/20 w-full font-bold text-white shadow-lg transition-all duration-300 hover:opacity-90"
                    >
                      <QrCode className="mr-2 size-4" />
                      เช็กอินตอนนี้
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {/* Table Number Section */}
        {tableNumber && (
          <section>
            <Card className="overflow-hidden border-none bg-stone-900 text-white">
              <CardContent className="flex items-center justify-between p-6">
                <div className="space-y-1">
                  <p className="text-xs font-medium tracking-widest text-stone-400 uppercase">
                    โต๊ะที่จัดไว้สำหรับคุณ
                  </p>
                  <h3 className="text-3xl font-bold">
                    โต๊ะหมายเลข {tableNumber}
                    {seatDisplay && (
                      <span className="block text-base font-normal text-stone-300 sm:ml-2 sm:inline">
                        · ที่นั่ง {seatDisplay}
                      </span>
                    )}
                  </h3>
                </div>
                <Link
                  to="/e/$eventId/map"
                  params={{ eventId }}
                  className="flex size-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20 active:scale-95"
                >
                  <MaterialSymbolsNavigationRounded className="size-8 rotate-45 text-white" />
                </Link>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Event Details Card */}
        {(displayWeddingDate || displayLocation) && (
          <section>
            <Card className="overflow-hidden border-none bg-white">
              <CardContent className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-stone-900">
                    ข้อมูล{copy.eventName}
                  </h3>
                  <div className="flex size-8 items-center justify-center rounded-full bg-stone-50 text-stone-500">
                    <Calendar className="size-4" />
                  </div>
                </div>

                <div className="space-y-4">
                  {displayWeddingDate && (
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-xl bg-stone-50 text-stone-500">
                        <Calendar className="size-5" />
                      </div>
                      <div>
                        <p className="font-bold text-stone-900">
                          {displayWeddingDate}
                        </p>
                        {s?.agenda && s.agenda.length > 0 && (
                          <p className="text-sm text-stone-500">
                            เริ่มงานเวลา {s.agenda[0].time} น.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {displayLocation && (
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-xl bg-stone-50 text-stone-500">
                        <MapPin className="size-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-stone-900">
                          {displayLocation}
                        </p>
                        {displayLocationDetail && (
                          <p className="mb-2 text-sm text-balance text-stone-500">
                            {displayLocationDetail}
                          </p>
                        )}
                        <div className="mt-2 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-full text-xs"
                            onClick={() =>
                              liff.openWindow({
                                url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayLocation)}`,
                                external: true,
                              })
                            }
                          >
                            <MapIcon className="mr-1.5 size-3" />
                            ดูแผนที่
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-full text-xs"
                            onClick={() =>
                              liff.openWindow({
                                url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(displayLocation)}`,
                                external: true,
                              })
                            }
                          >
                            <Navigation className="mr-1.5 size-3" />
                            นำทาง
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Quick Actions Grid (Filter based on enabled modules) */}
        {(showBlessing || showLuckyDraw || showAngpao) && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-stone-900">กิจกรรม</h3>
              <Link
                to="/e/$eventId/activities"
                params={{ eventId }}
                className="flex items-center text-xs font-bold tracking-wider text-stone-500 uppercase"
              >
                ดูทั้งหมด <ChevronRight className="ml-0.5 size-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {showBlessing && (
                <Link
                  to="/e/$eventId/activities/wedding-blessing"
                  params={{ eventId }}
                  className="group relative overflow-hidden rounded-3xl bg-white p-6 shadow-sm transition-all hover:shadow-md active:scale-95"
                >
                  <div className="bg-primary/10 text-primary group-hover:bg-primary/20 mb-4 flex size-12 items-center justify-center rounded-2xl transition-colors">
                    <Heart className="size-6 fill-current" />
                  </div>
                  <p className="font-bold text-stone-950">
                    {copy.blessingTitle}
                  </p>
                  <p className="text-xs text-stone-500">
                    {copy.blessingSubtitle}
                  </p>
                </Link>
              )}

              {showLuckyDraw && (
                <Link
                  to="/e/$eventId/activities/random-number"
                  params={{ eventId }}
                  className="group relative overflow-hidden rounded-3xl bg-white p-6 shadow-sm transition-all hover:shadow-md active:scale-95"
                >
                  <div className="bg-secondary/10 text-secondary group-hover:bg-secondary/20 mb-4 flex size-12 items-center justify-center rounded-2xl transition-colors">
                    <MdiDice5Outline className="size-6" />
                  </div>
                  <p className="font-bold text-stone-950">กิจกรรมสุ่มเลข</p>
                  <p className="text-xs text-stone-500">ลุ้นรางวัลผู้โชคดี</p>
                </Link>
              )}

              {showAngpao && (
                <Link
                  to="/e/$eventId/activities/e-angpao"
                  params={{ eventId }}
                  className="group relative overflow-hidden rounded-3xl bg-white p-6 shadow-sm transition-all hover:shadow-md active:scale-95"
                >
                  <div className="bg-primary/10 text-primary group-hover:bg-primary/20 mb-4 flex size-12 items-center justify-center rounded-2xl transition-colors">
                    <Gift className="size-6" />
                  </div>
                  <p className="font-bold text-stone-950">ของขวัญ (E-Angpao)</p>
                  <p className="text-xs text-stone-500">
                    {copy.angpaoSubtitle}
                  </p>
                </Link>
              )}
            </div>
          </section>
        )}

        {/* Timeline Preview */}
        {agendaList.length > 0 && (
          <section className="space-y-4 pb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-stone-900">กำหนดการ</h3>
              <Link
                to="/e/$eventId/agenda"
                params={{ eventId }}
                className="flex items-center text-xs font-bold tracking-wider text-stone-500 uppercase"
              >
                ดูทั้งหมด <ChevronRight className="ml-0.5 size-3" />
              </Link>
            </div>

            <div className="relative space-y-6 pl-10">
              <div className="absolute top-0 bottom-0 left-5 w-0.5 bg-stone-100" />

              {(() => {
                const currentIdx = agendaList.findIndex(
                  (item: any) => item.id === currentAgendaId,
                )
                const displayItems =
                  currentIdx === -1
                    ? agendaList.slice(0, 3)
                    : agendaList.slice(currentIdx, currentIdx + 3)

                return displayItems.map((item: any) => {
                  const isCurrent = item.id === currentAgendaId

                  return (
                    <div
                      key={item.id}
                      className="relative flex items-center gap-6"
                    >
                      <div
                        className={cn(
                          'absolute -left-7 box-content flex size-2.5 items-center justify-center rounded-full border-4 border-white shadow transition-all',
                          isCurrent ? 'bg-primary scale-125' : 'bg-stone-200',
                        )}
                      />
                      <div
                        className={cn(
                          'flex-1 rounded-2xl p-4 shadow-sm transition-all',
                          isCurrent
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white',
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={cn(
                              'text-[10px] font-bold uppercase',
                              isCurrent
                                ? 'text-primary-foreground/80'
                                : 'text-stone-400',
                            )}
                          >
                            {item.time} น.
                          </span>
                          {isCurrent && (
                            <span className="flex items-center gap-1 text-[8px] font-bold text-white uppercase">
                              <span className="size-1 animate-pulse rounded-full bg-white" />
                              กำลังดำเนินอยู่
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            'text-sm font-bold sm:text-base',
                            isCurrent ? 'text-white' : 'text-stone-900',
                          )}
                        >
                          {item.title}
                        </p>
                      </div>
                    </div>
                  )
                })
              })()}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
